/**
 * ClaimsList Component
 *
 * Displays a responsive list of enriched claims with pagination.
 * Uses CSS Grid layout that adapts from full table to stacked cards on mobile.
 * Mirrors the BillingEncountersList pattern.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DeleteConfirmDialog } from '@components/DeleteConfirmDialog';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  IconButton,
  Paper,
  Skeleton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { ClaimListItem } from '@store/generated/billing';
import { useSnackbar } from 'notistack';

import { useBillingClaimsList, useClaimSubmission, useClaimSubmit, useLazyGetClaimPdfQuery, useLazyGetStediPdfQuery, useResubmitClaim } from '../../../store';
import { downloadBase64Pdf } from '../../../utils';

import { ClaimRow } from './ClaimRow';
import { ClaimTableHeader } from './ClaimTableHeader';
import { ResubmitClaimDialog } from './ResubmitClaimDialog';
import type { TableLayoutMode } from './types';
import { getGridTemplateColumns, GRID_GAP } from './types';

interface ClaimsListProps {
  filters: {
    patient?: string;
    from?: string;
    to?: string;
    status?: string;
    providers?: string;
  };
  sort?: string;
  order: 'asc' | 'desc';
  page: number;
  perPage: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  onSort: (field: string) => void;
  onSelectedClaimsChange?: (claims: ClaimListItem[]) => void;
}

// Map UI sort fields to API sort field names
const SORT_FIELD_MAP: Record<string, 'created_at' | 'service_date_from' | 'total_charge_cents'> = {
  dateOfService: 'service_date_from',
  service_date_from: 'service_date_from',
  dateCreated: 'created_at',
  created_at: 'created_at',
  amount: 'total_charge_cents',
  total_charge_cents: 'total_charge_cents',
};

export const ClaimsList: React.FC<ClaimsListProps> = ({
  filters,
  sort,
  order,
  page,
  perPage,
  onPageChange,
  onSelectedClaimsChange,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // Responsive table layout
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  const tableLayout: TableLayoutMode = useMemo(() => {
    if (isXl) return 'full';
    if (isLg) return 'standard';
    if (isMd) return 'compact';
    return 'card';
  }, [isXl, isLg, isMd]);

  // Selection tracking
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Action state
  const [getClaimPdf] = useLazyGetClaimPdfQuery();
  const [getStediPdf] = useLazyGetStediPdfQuery();
  const { deleteClaim } = useClaimSubmission();
  const { submitClaim, isSubmitting } = useClaimSubmit();
  const [submittingClaimId, setSubmittingClaimId] = useState<string | null>(null);
  const [downloadingClaimIds, setDownloadingClaimIds] = useState<Set<string>>(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [claimToDelete, setClaimToDelete] = useState<ClaimListItem | null>(null);

  // Resubmit (correction/void) state
  const { resubmitClaim, isResubmitting } = useResubmitClaim();
  const [resubmitTarget, setResubmitTarget] = useState<{ claim: ClaimListItem; code: '7' | '8' } | null>(null);

  // Polling for processing claims
  const [needsPolling, setNeedsPolling] = useState(false);

  // Map sort field
  const apiSortBy = sort && SORT_FIELD_MAP[sort] ? SORT_FIELD_MAP[sort] : undefined;

  const {
    claims,
    totalCount,
    currentPage,
    totalPages,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useBillingClaimsList(
    {
      patientId: filters.patient,
      providerIds: filters.providers,
      status: filters.status,
      dateFrom: filters.from,
      dateTo: filters.to,
      page,
      perPage,
      sortBy: apiSortBy,
      sortOrder: apiSortBy ? order : undefined,
    },
    needsPolling ? 2000 : undefined,
  );

  // Update polling state — only poll for transient statuses (processing/scrubbing)
  // Don't poll for 'submitted' which is a stable state awaiting external response
  useEffect(() => {
    const hasTransient = claims.some(c => c.status === 'processing' || c.status === 'scrubbing');
    setNeedsPolling(hasTransient);
  }, [claims]);

  // Notify parent of selection changes
  useEffect(() => {
    if (onSelectedClaimsChange) {
      const selectedClaims = claims.filter(c => selectedIds.has(c.claim_id));
      onSelectedClaimsChange(selectedClaims);
    }
  }, [selectedIds, claims, onSelectedClaimsChange]);

  const isEmpty = !isLoading && claims.length === 0;
  const isListLoading = isLoading || isFetching;
  const skeletonRowCount = Math.min(12, Math.max(6, perPage));

  // Pagination
  const hasPrevious = currentPage > 1;
  const hasMore = currentPage < totalPages;

  const handlePreviousPage = useCallback(() => {
    if (hasPrevious && !isLoading) {
      onPageChange(Math.max(1, currentPage - 1));
    }
  }, [hasPrevious, isLoading, currentPage, onPageChange]);

  const handleNextPage = useCallback(() => {
    if (hasMore && !isLoading) {
      onPageChange(currentPage + 1);
    }
  }, [hasMore, isLoading, currentPage, onPageChange]);

  // Selection handlers
  const handleSelectChange = useCallback(
    (claimId: string, selected: boolean) => {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (selected) {
          next.add(claimId);
        } else {
          next.delete(claimId);
        }
        return next;
      });
    },
    [],
  );

  // Row click → post-submission claims go to encounter detail, pre-submission to claim edit
  const handleRowClick = useCallback(
    (claim: ClaimListItem) => {
      const postSubmissionStatuses = ['submitted', 'acknowledged', 'accepted', 'rejected', 'paid', 'partially_paid', 'denied', 'reversed', 'imported'];
      if (postSubmissionStatuses.includes(claim.status) && claim.encounter_id) {
        navigate(`/billing/encounter/${claim.encounter_id}`);
      } else if (postSubmissionStatuses.includes(claim.status)) {
        navigate(`/billing/claim/${claim.claim_id}`);
      } else {
        navigate(`/billing/claim/${claim.claim_id}/edit`);
      }
    },
    [navigate],
  );

  // Submit handler
  const handleSubmit = useCallback(
    async (claimId: string) => {
      setSubmittingClaimId(claimId);
      const result = await submitClaim(claimId);
      if (result.success) {
        if (result.submissionBlocked) {
          enqueueSnackbar('Submission blocked. Please review and fix validation errors.', { variant: 'warning' });
        } else {
          enqueueSnackbar('Claim submitted successfully', { variant: 'success' });
        }
      } else {
        enqueueSnackbar('Failed to submit claim. Please try again.', { variant: 'error' });
      }
      setSubmittingClaimId(null);
    },
    [submitClaim, enqueueSnackbar],
  );

  // Delete handler
  const handleDelete = useCallback(
    (claim: ClaimListItem) => {
      setClaimToDelete(claim);
      setDeleteDialogOpen(true);
    },
    [],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (claimToDelete) {
      const result = await deleteClaim(claimToDelete.claim_id);
      if (result.success) {
        enqueueSnackbar('Claim deleted successfully', { variant: 'success' });
        refetch();
      } else {
        enqueueSnackbar('Failed to delete claim', { variant: 'error' });
      }
      setDeleteDialogOpen(false);
      setClaimToDelete(null);
    }
  }, [claimToDelete, deleteClaim, enqueueSnackbar, refetch]);

  // PDF download handler
  const handleDownloadPdf = useCallback(
    async (claimId: string) => {
      setDownloadingClaimIds(prev => new Set(prev).add(claimId));
      try {
        let result;
        try {
          result = await getStediPdf({ claimId, background: true }).unwrap();
        } catch {
          result = await getClaimPdf({ claimId }).unwrap();
        }
        const filename = result.filename && !result.filename.includes('None')
          ? result.filename
          : `claim_${claimId}.pdf`;
        downloadBase64Pdf(result.pdf_data, filename);
        enqueueSnackbar('PDF downloaded successfully', { variant: 'success' });
      } catch {
        enqueueSnackbar('Failed to download PDF', { variant: 'error' });
      } finally {
        setDownloadingClaimIds(prev => {
          const next = new Set(prev);
          next.delete(claimId);
          return next;
        });
      }
    },
    [getStediPdf, getClaimPdf, enqueueSnackbar],
  );

  // Resubmit handlers
  const handleResubmit = useCallback(
    (claim: ClaimListItem, code: '7' | '8') => {
      setResubmitTarget({ claim, code });
    },
    [],
  );

  const handleConfirmResubmit = useCallback(
    async (originalRefNumber?: string) => {
      if (!resubmitTarget) return;
      const { claim, code } = resubmitTarget;
      const result = await resubmitClaim(claim.claim_id, code, originalRefNumber);
      if (result.success && result.data) {
        const label = code === '7' ? 'Correction' : 'Void';
        enqueueSnackbar(`${label} claim created successfully`, { variant: 'success' });
        setResubmitTarget(null);
        navigate(`/billing/claim/${result.data.id}/edit`);
      } else {
        enqueueSnackbar('Failed to create resubmission claim', { variant: 'error' });
      }
    },
    [resubmitTarget, resubmitClaim, enqueueSnackbar, navigate],
  );

  // Skeleton rows
  const renderSkeletonRow = (key: number) => {
    if (tableLayout === 'card') {
      return (
        <Box
          key={key}
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: `1px solid ${theme.palette.grey[100]}`,
            '&:last-child': { borderBottom: 'none' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
            <Skeleton variant="circular" width={28} height={28} />
            <Skeleton variant="text" width="30%" />
            <Skeleton variant="rectangular" width={56} height={18} sx={{ borderRadius: 999 }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 0.5 }}>
            <Skeleton variant="text" width="20%" />
            <Skeleton variant="text" width="15%" />
            <Skeleton variant="text" width="12%" />
          </Box>
          <Skeleton variant="text" width="50%" />
        </Box>
      );
    }

    return (
      <Box
        key={key}
        sx={{
          px: 2,
          py: 0.75,
          borderBottom: `1px solid ${theme.palette.grey[100]}`,
          '&:last-child': { borderBottom: 'none' },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: getGridTemplateColumns(tableLayout),
            gap: GRID_GAP,
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="text" width="70%" />
          </Box>
          {(tableLayout === 'full' || tableLayout === 'standard') && <Skeleton variant="text" width="80%" />}
          {tableLayout === 'full' && <Skeleton variant="text" width="75%" />}
          {(tableLayout === 'full' || tableLayout === 'standard') && <Skeleton variant="text" width="60%" />}
          <Skeleton variant="text" width="85%" />
          <Skeleton variant="text" width="70%" sx={{ textAlign: 'right' }} />
          <Skeleton variant="rectangular" width={50} height={18} sx={{ borderRadius: 999 }} />
          {(tableLayout === 'full' || tableLayout === 'standard') && <Skeleton variant="text" width="50%" />}
          {tableLayout === 'full' && <Skeleton variant="circular" width={12} height={12} />}
          <Skeleton variant="circular" width={18} height={18} />
        </Box>
      </Box>
    );
  };

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={refetch}>
            Retry
          </Button>
        }
      >
        <AlertTitle>Error loading claims</AlertTitle>
        Failed to load billing claims. Please try again.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Summary */}
      <Box sx={{ mb: 2 }}>
        {!isListLoading && (
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {totalCount} {totalCount === 1 ? 'claim' : 'claims'}
            {isFetching && !isLoading && ' (updating...)'}
          </Typography>
        )}
      </Box>

      {/* Claims Table */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <ClaimTableHeader layout={tableLayout} />

        {isListLoading ? (
          <Box>
            {Array.from({ length: skeletonRowCount }).map((_, i) => renderSkeletonRow(i))}
          </Box>
        ) : isEmpty ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              px: 2,
            }}
          >
            <Typography variant="h6" sx={{ mb: 1, color: theme.palette.text.secondary }}>
              No claims found
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
              Try adjusting your filters or date range
            </Typography>
          </Box>
        ) : (
          <Box>
            {claims.map(claim => (
              <ClaimRow
                key={claim.claim_id}
                claim={claim}
                layout={tableLayout}
                selected={selectedIds.has(claim.claim_id)}
                onSelectChange={onSelectedClaimsChange ? handleSelectChange : undefined}
                onRowClick={handleRowClick}
                onSubmit={handleSubmit}
                onDelete={handleDelete}
                onDownloadPdf={handleDownloadPdf}
                onResubmit={handleResubmit}
                isSubmitting={isSubmitting}
                submittingClaimId={submittingClaimId}
                downloadingClaimIds={downloadingClaimIds}
              />
            ))}
          </Box>
        )}

        {/* Pagination */}
        {!isEmpty && !isListLoading && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              borderTop: `1px solid ${theme.palette.grey[200]}`,
              bgcolor: theme.palette.grey[50],
            }}
          >
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              Page {currentPage} of {totalPages} ({totalCount} total)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton onClick={handlePreviousPage} disabled={!hasPrevious || isFetching} size="small">
                <ChevronLeftIcon />
              </IconButton>
              <IconButton onClick={handleNextPage} disabled={!hasMore || isFetching} size="small">
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </Box>
        )}
      </Paper>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        type="claim"
        onClose={() => {
          setDeleteDialogOpen(false);
          setClaimToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
      />

      <ResubmitClaimDialog
        open={resubmitTarget !== null}
        onClose={() => setResubmitTarget(null)}
        onConfirm={handleConfirmResubmit}
        mode={resubmitTarget?.code === '8' ? 'void' : 'correction'}
        isSubmitting={isResubmitting}
        defaultRefNumber={resubmitTarget?.claim.patient_control_number || undefined}
      />
    </Box>
  );
};
